import { useEffect, useRef } from 'react';

import { DRAG_THRESHOLD_PX } from './event-drag';

export interface DragGestureOptions {
  // Body cursor while the drag is active ('grabbing', 'ns-resize', …).
  cursor?: string;
  // 'y': only vertical movement can activate the drag (resize handles).
  axis?: 'both' | 'y';
  // Called on every mousemove after the activation threshold is passed.
  onMove: (ev: MouseEvent) => void;
  // Called on mouseup, but only if the drag activated.
  onEnd: (ev: MouseEvent) => void;
  // Called on mouseup when the pointer never passed the threshold — a plain
  // click. Use to clear any provisional drag state.
  onCancel?: () => void;
}

// Shared scaffolding for mouse-drag gestures on calendar events: threshold
// activation (so clicks stay clicks), document-level listener wiring with
// cleanup on unmount (navigating views mid-drag must not leak listeners into
// stale closures), body-cursor management, and swallowing the synthesised
// click that follows a completed drag.
export function useDragGesture() {
  const wasDraggedRef = useRef(false);
  const activeCleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      activeCleanupRef.current?.();
    },
    []
  );

  // True exactly once after a completed drag — callers use it to swallow the
  // click event the browser synthesises from the terminating mouseup.
  function consumeDragClick(): boolean {
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return true;
    }
    return false;
  }

  function startDrag(ev: React.MouseEvent, opts: DragGestureOptions): void {
    if (ev.button !== 0) return;
    const startX = ev.clientX;
    const startY = ev.clientY;
    let activated = false;

    function handleMove(mev: MouseEvent) {
      if (!activated) {
        const dx = Math.abs(mev.clientX - startX);
        const dy = Math.abs(mev.clientY - startY);
        const passed =
          opts.axis === 'y'
            ? dy >= DRAG_THRESHOLD_PX
            : dx >= DRAG_THRESHOLD_PX || dy >= DRAG_THRESHOLD_PX;
        if (!passed) return;
        activated = true;
        if (opts.cursor) document.body.style.cursor = opts.cursor;
      }
      opts.onMove(mev);
    }

    function cleanup() {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      activeCleanupRef.current = null;
    }

    function handleUp(mev: MouseEvent) {
      cleanup();
      if (!activated) {
        opts.onCancel?.();
        return;
      }
      wasDraggedRef.current = true;
      // Reset on the next tick so only the click synthesised from this
      // mouseup gets swallowed.
      window.setTimeout(() => {
        wasDraggedRef.current = false;
      }, 0);
      opts.onEnd(mev);
    }

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    activeCleanupRef.current = cleanup;
  }

  return { startDrag, consumeDragClick };
}
